import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

const PlayerContainer = styled.div`
  width: 100%;
`;

const ProgressBar = styled.input`
  width: 100%;
  appearance: none;
  height: 6px;
  background: #f1f1f1;
  border-radius: 3px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    background: #3b82f6;
    border-radius: 50%;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    background: #3b82f6;
    border-radius: 50%;
    cursor: pointer;
  }
`;

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds) || !isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

const AudioPlayer = React.forwardRef(({
  audioUrl,
  title = "Audio Recording",
  imageUrl = "/placeholder.svg",
  startTime = 0,
  className = "",
  onPlay = null,
  isExpertAudio = false,
  onPause = null,
  onTimeUpdate = null,
  onLoadedMetadata = null,
  onSeek = null,
  onEnded = null,
  volume = 1,
}, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  React.useImperativeHandle(ref, () => ({
    play: () => setIsPlaying(true),
    pause: () => setIsPlaying(false),
    stop: () => {
      setIsPlaying(false);
      if (audioRef.current) {
        audioRef.current.currentTime = startTime;
      }
      setCurrentTime(startTime);
    },
    toggle: () => setIsPlaying(prev => !prev),
    getCurrentTime: () => (audioRef.current ? audioRef.current.currentTime : 0),
    seek: (time) => {
      if (audioRef.current && isFinite(time)) {
        audioRef.current.currentTime = time;
        setCurrentTime(time);
      }
    },
    getIsPlaying: () => isPlaying
  }));
  const [currentTime, setCurrentTime] = useState(startTime);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const audioRef = useRef(null);
  const hasSetInitialTime = useRef(false);
  const hasTriggeredPlayEvent = useRef(false);

  useEffect(() => {
    audioRef.current = new Audio();
    const audio = audioRef.current;
    hasSetInitialTime.current = false;
    hasTriggeredPlayEvent.current = false;

    setIsPlaying(false);
    setCurrentTime(startTime);
    setDuration(0);
    setIsLoaded(false);

    if (audioUrl) {
      audio.src = audioUrl;
      audio.load();

      const fallbackTimer = setTimeout(() => {
        if (!isLoaded && audioUrl.startsWith("data:audio")) {
          console.log("Using fallback loader for base64 audio");
          setIsLoaded(true);
          if (!duration || duration === 0) {
            setDuration(100);
          }
          
          if (!hasSetInitialTime.current && startTime > 0) {
            audio.currentTime = startTime;
            setCurrentTime(startTime);
            hasSetInitialTime.current = true;
          }
        }
      }, 1000);

      const setAudioData = () => {
        console.log("Audio metadata loaded, duration:", audio.duration);
        if (audio.duration && isFinite(audio.duration)) {
          setDuration(audio.duration);
          setIsLoaded(true);
          
          if (onLoadedMetadata) {
            onLoadedMetadata({
              duration: audio.duration,
              audioElement: audio
            });
          }
          
          if (!hasSetInitialTime.current && startTime > 0) {
            audio.currentTime = startTime;
            setCurrentTime(startTime);
            hasSetInitialTime.current = true;
            console.log(`Audio starting position set to ${startTime}s`);
          }
        }
      };

      // 🔧 FIX: SINGLE time update handler (NO DUPLICATES)
      const setAudioTime = () => {
        if (isFinite(audio.currentTime)) {
          const time = audio.currentTime;
          setCurrentTime(time);
          
          if (onTimeUpdate) {
            // ✅ SINGLE SOURCE OF TRUTH - ONLY ONE CALL
            onTimeUpdate(time);
          }
        }
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(startTime);
        if (isExpertAudio && onPause) onPause(audio);
        if (onEnded) onEnded();
      };

      audio.addEventListener("loadedmetadata", setAudioData);
      audio.addEventListener("canplaythrough", setAudioData);
      audio.addEventListener("timeupdate", setAudioTime);
      audio.addEventListener("ended", handleEnded);
      
      // 🔧 FIX: Remove duplicate time update in seeked
      audio.addEventListener("seeked", () => {
        // Only update internal state, don't call onTimeUpdate again
        if (isFinite(audio.currentTime)) {
          setCurrentTime(audio.currentTime);
        }
      });

      return () => {
        clearTimeout(fallbackTimer);
        audio.removeEventListener("loadedmetadata", setAudioData);
        audio.removeEventListener("canplaythrough", setAudioData);
        audio.removeEventListener("timeupdate", setAudioTime);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("seeked", setAudioTime);
        audio.pause();
      };
    }
  }, [audioUrl, startTime]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = Math.min(1, Math.max(0, volume));
    }
  }, [volume, audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      if (startTime > 0 && !hasSetInitialTime.current) {
        audio.currentTime = startTime;
        hasSetInitialTime.current = true;
      }
      
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log("Audio playing successfully");
            
            if (isExpertAudio && onPlay) {
              onPlay();
              hasTriggeredPlayEvent.current = true;
              console.log("Expert audio play event triggered");
            }
          })
          .catch((error) => {
            console.error("Error playing audio:", error);
            setIsPlaying(false);
          });
      }
    } else {
      if (audio) {
        audio.pause();
      }
      
      if (isExpertAudio && onPause) {
        onPause();
        hasTriggeredPlayEvent.current = false;
      }
    }
  }, [isPlaying, startTime, onPlay, onPause, isExpertAudio]);

  const togglePlayPause = () => {
    console.log("Toggle play/pause button clicked, current state:", isPlaying);
    
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);
  };

  const handleProgressChange = (event) => {
    const audio = audioRef.current;
    if (!audio) return;

    const value = parseFloat(event.target.value);
    const availableDuration = duration - startTime;
    const newTime = startTime + ((availableDuration) / 100) * value;

    if (isFinite(newTime)) {
      audio.currentTime = newTime;
      setCurrentTime(newTime);
      
      if (onSeek) {
        onSeek({
          currentTime: newTime,
          audioElement: audio
        });
      }
    }
  };

  const progress = duration > startTime 
    ? ((currentTime - startTime) / (duration - startTime)) * 100 
    : 0;

  const displayCurrentTime = currentTime - startTime < 0 ? 0 : currentTime - startTime;
  const displayDuration = duration - startTime;

  return (
    <PlayerContainer className={className}>
      <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm flex items-center gap-2 sm:gap-3">
        {/* Removed image placeholder as requested */}

        <div className="flex-1 min-w-0">
          <div className="text-sm sm:text-base font-bold mb-1 sm:mb-2 truncate text-slate-800">
            {title}
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={togglePlayPause}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 hover:bg-blue-700 transition-all duration-300 shadow-md shadow-blue-200"
              aria-label={isPlaying ? "Pause" : "Play"}>
              {isPlaying ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  viewBox="0 0 24 24"
                  fill="currentColor">
                  <rect x="6" y="5" width="4" height="14" fill="currentColor" />
                  <rect x="14" y="5" width="4" height="14" fill="currentColor" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white ml-0.5"
                  viewBox="0 0 24 24"
                  fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            <div className="flex-1 min-w-0">
              <ProgressBar
                type="range"
                min="0"
                max="100"
                step="0.1"
                value={progress}
                onChange={handleProgressChange}
                className="w-full"
              />
            </div>

            <div className="hidden xs:block text-xs text-gray-600 min-w-[60px] sm:min-w-[70px] text-right whitespace-nowrap">
              {formatTime(displayCurrentTime)} / {formatTime(displayDuration)}
            </div>

            <div className="xs:hidden text-xs text-gray-600 min-w-[35px] text-right">
              {formatTime(displayCurrentTime)}
            </div>
          </div>
        </div>
      </div>
    </PlayerContainer>
  );
});

export default AudioPlayer;